import React, { useEffect, useState } from 'react';
import "../../assets/style/eegisterProvider.css";
import { Button, Typography, Chip } from '@mui/material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ApiCall from '../../utils/Api';
import { toast } from 'react-toastify';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { CircularProgress } from '@mui/material';
import Web3 from 'web3';
import FundDataUnitTransactions from './FundDataUnitTransactions';

const FundSubscriberTransactions = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [fundLevels, setFundLevels] = useState([]);
  const [subscriberName, setSubscriberName] = useState('');
  const [loading, setLoading] = useState(true);
  const [ethPrice, setEthPrice] = useState(2000);
  const [showDataUnitTransactions, setShowDataUnitTransactions] = useState(false);
  const [userRole, setUserRole] = useState('Admin');

  useEffect(() => {
    // Get user role from localStorage
    const role = localStorage.getItem('role') || 'Admin';
    setUserRole(role);
    
    // Fetch ETH price
    const fetchEthPrice = async () => {
      try {
        const response = await ApiCall.Api.get('/thirdPartyApi/getCryptoData');
        const ethereumData = response.data.data['1027'];
        const usdPrice = ethereumData.quote.USD.price;
        setEthPrice(usdPrice);
      } catch (error) {
        console.error('Error fetching ETH price:', error);
      }
    };
    fetchEthPrice();
  }, []);

  useEffect(() => {
    const fetchFundLevels = async () => {
      let dataLoaded = false;
      try {
        setLoading(true);
        
        // Fetch buyer data to get fund subscriptions
        const response = await ApiCall.SECURE_API.get(`/buyer/getAllBuyers`);
        
        if (response.data.success && response.data.data) {
          dataLoaded = true;
          // Determine if ID is a data unit ID or subscriber ID
          // Try to find purchases by data unit first (from Fund Transactions table)
          let subscriberPurchases = response.data.data.filter(purchase => {
            const dataUnitId = purchase?.dataUnit?._id || purchase?.dataUnit || purchase?.dataUnitId;
            return String(dataUnitId) === String(id);
          });
          
          // Check if this is a data unit ID
          let isDataUnitId = false;
          if (subscriberPurchases.length > 0) {
            const firstPurchase = subscriberPurchases[0];
            const dataUnitId = firstPurchase?.dataUnit?._id || firstPurchase?.dataUnit || firstPurchase?.dataUnitId;
            isDataUnitId = String(dataUnitId) === String(id);
          }
          
          // If it's a data unit ID, show the transaction list component instead
          if (isDataUnitId) {
            setShowDataUnitTransactions(true);
            setLoading(false);
            return;
          }
          
          // If no matches found, treat ID as subscriber/buyer ID
          if (subscriberPurchases.length === 0) {
            subscriberPurchases = response.data.data.filter(purchase => {
              const buyerId = purchase?.BuyerConsumer?._id || purchase?.BuyerConsumer || purchase?.BuyerProvider?._id || purchase?.BuyerProvider;
              return String(buyerId) === String(id);
            });
          }

          // Get subscriber/fund name based on what we're showing
          if (subscriberPurchases.length > 0) {
            const firstPurchase = subscriberPurchases[0];
            // If filtering by data unit, show the fund/data unit name
            const dataUnitId = firstPurchase?.dataUnit?._id || firstPurchase?.dataUnit || firstPurchase?.dataUnitId;
            if (String(dataUnitId) === String(id)) {
              // Show data unit/fund name
              const fundName = firstPurchase?.fundUnitName || firstPurchase?.dataUnit?.fundUnitName || firstPurchase?.fundName || firstPurchase?.dataUnit?.name || 'Fund';
              setSubscriberName(fundName);
            } else {
              // Show subscriber name
              const buyer = firstPurchase.BuyerConsumer || firstPurchase.BuyerProvider;
              const name = buyer?.firstName && buyer?.lastName 
                ? `${buyer.firstName} ${buyer.lastName}`
                : buyer?.institutionName || 'Fund Subscriber';
              setSubscriberName(name);
            }
          } else {
            setSubscriberName('Fund');
          }

          // Group purchases by fund and level
          const fundLevelMap = new Map();

          subscriberPurchases.forEach(purchase => {
            const fundName = purchase?.fundUnitName || purchase?.dataUnit?.fundUnitName || purchase?.fundName || 'Unknown Fund';
            const pricingLevels = purchase?.dataUnit?.pricingLevels || [];
            const selectedLevel = purchase?.selectedLevel || {};
            const levelName = selectedLevel?.levelName || '1';
            const ticketsPurchased = purchase?.ticketsPurchased || 1;
            
            // Get transaction hash for blockchain explorer link
            const transactionHash = purchase?.transactionHash || '';
            
            // Calculate investment per ticket
            const ticketUSD = selectedLevel?.ticketUSD || purchase?.dataUnit?.pricingLevels?.[0]?.ticketUSD || 0;
            const ticketETH = selectedLevel?.ticketETH || purchase?.dataUnit?.pricingLevels?.[0]?.ticketETH || 0;
            
            // If no ticket ETH, calculate from USD
            let calculatedTicketETH = ticketETH;
            if (!calculatedTicketETH && ticketUSD && ethPrice) {
              calculatedTicketETH = ticketUSD / ethPrice;
            }
            
            const annualYield = selectedLevel?.annualYieldPercent || purchase?.dataUnit?.pricingLevels?.[0]?.annualYieldPercent || 0;
            
            const key = `${fundName}-Level-${levelName}`;
            
            if (fundLevelMap.has(key)) {
              const existing = fundLevelMap.get(key);
              existing.ticketsPurchased += ticketsPurchased;
              existing.investmentETH += calculatedTicketETH * ticketsPurchased;
              existing.investmentUSD += ticketUSD * ticketsPurchased;
              // Keep the transaction hash (prefer the first one found)
              if (!existing.transactionHash && transactionHash) {
                existing.transactionHash = transactionHash;
              }
            } else {
              fundLevelMap.set(key, {
                fundName,
                levelName: `Level -${levelName}`,
                ticketsPurchased,
                investmentETH: calculatedTicketETH * ticketsPurchased,
                investmentUSD: ticketUSD * ticketsPurchased,
                annualYield,
                dataUnitId: purchase?.dataUnit?._id || purchase?.dataUnitId,
                fundUnitName: fundName,
                transactionHash: transactionHash
              });
            }
          });

          // Convert map to array and sort by fund name, then level
          const fundLevelsArray = Array.from(fundLevelMap.values());
          
          // Group by fund name for better organization
          const groupedByFund = {};
          fundLevelsArray.forEach(item => {
            if (!groupedByFund[item.fundName]) {
              groupedByFund[item.fundName] = [];
            }
            groupedByFund[item.fundName].push(item);
          });

          // Flatten and sort
          const sortedLevels = [];
          Object.keys(groupedByFund).sort().forEach(fundName => {
            groupedByFund[fundName].sort((a, b) => {
              const levelA = parseInt(a.levelName.replace('Level -', '')) || 0;
              const levelB = parseInt(b.levelName.replace('Level -', '')) || 0;
              return levelA - levelB;
            });
            sortedLevels.push(...groupedByFund[fundName]);
          });

          setFundLevels(sortedLevels);
        }
      } catch (error) {
        console.error("Error fetching fund levels:", error);
        console.error("Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        
        // Only show error if data wasn't loaded in this attempt AND we don't have existing data
        if (!dataLoaded && fundLevels.length === 0) {
          toast.error(error.response?.data?.message || "Failed to fetch fund subscriber details");
        } else if (!dataLoaded) {
          // If we have existing data but this fetch failed, just log it
          console.warn("Error fetching fresh data, keeping existing fund levels");
        }
        // Don't clear fundLevels on error - keep existing data
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchFundLevels();
    }
  }, [id, ethPrice]);

  const goBack = () => {
    navigate(-1);
  };

  const formatEth = (eth) => {
    return typeof eth === 'number' ? eth.toFixed(6) : '0.000000';
  };

  const formatUsd = (usd) => {
    return typeof usd === 'number' ? usd.toFixed(2) : '0.00';
  };

  // If it's a data unit ID, show the transaction list component
  if (showDataUnitTransactions) {
    return <FundDataUnitTransactions />;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ width: "100%", backgroundColor: "#F3F4F8", minHeight: "100vh" }}>
      {/* Header with title and Provider badge */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '20px 40px',
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button 
            variant="contained" 
            onClick={goBack} 
            style={{ marginRight: "20px", backgroundColor: "black" }}
          >
            {"< Back"}
          </Button>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Fund Subscribers - {subscriberName} - Details (I)
          </Typography>
        </div>
        <Chip 
          label={userRole || 'Admin'} 
          sx={{ 
            backgroundColor: userRole === 'Admin' ? '#e3f2fd' : '#fce4ec', 
            color: userRole === 'Admin' ? '#1976d2' : '#c2185b',
            fontWeight: 600,
            fontSize: '12px'
          }} 
        />
      </div>
      
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-around", width: "100%", padding: '40px' }}>
        <div style={{ width: "98%" }}>
          <TableContainer component={Paper} style={{ borderRadius: 9 }}>
            <Table sx={{ minWidth: 1200 }} aria-label="fund level details">
              <TableHead style={{ backgroundColor: "Black" }}>
                <TableRow>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Fund</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Fund Level</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Purchased Tickets</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Investment per Level</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Annual Yield</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fundLevels.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" style={{ padding: '40px' }}>
                      <Typography variant="body1" color="textSecondary">
                        No fund subscriptions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  fundLevels.map((level, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#f0f8ff' }
                      }}
                    >
                      <TableCell align="center" style={{ fontSize: '14px', fontWeight: 500 }}>
                        {level.fundName}
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px' }}>
                        {level.levelName}
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px' }}>
                        {level.ticketsPurchased}
                      </TableCell>
                      <TableCell align="center">
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>
                            {formatEth(level.investmentETH)} ETH
                          </span>
                          <span style={{ color: "gray", fontSize: '13px' }}>
                            {formatUsd(level.investmentUSD)} USD
                          </span>
                        </div>
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px' }}>
                        {level.annualYield}%
                      </TableCell>
                      <TableCell align="center">
                        <Link
                          to={`/admin-panel/FundSubscriberTransactions/${id}/${encodeURIComponent(level.fundName)}/${encodeURIComponent(level.levelName)}`}
                          style={{ 
                            color: '#1976d2', 
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          Transactions
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default FundSubscriberTransactions;
